import { useCallback, useEffect, useRef, useState } from 'react';
import type { FundingResponse, FundingRow } from '../../shared/contract';
import { loadWithFallback } from '../lib/api';
import { basePath } from '../lib/paths';
import { fmtCountdown, fmtPct, fmtRate, clamp } from '../lib/format';
import { log } from '../lib/log';
import { identity, story } from '../content';
import fundingSnapshot from '../data/funding.json';
import { Book, type BookState } from './Book';
import { Chars, useInView } from './motion';
import { navigate } from '../Router';
import './Site.css';

const FUNDING_FALLBACK = fundingSnapshot as unknown as FundingResponse;
const STRIP_SYMBOLS = 4;

function pickExtremes(rows: readonly FundingRow[]): FundingRow[] {
  const sorted = [...rows].sort((a, b) => b.annualisedRate - a.annualisedRate);
  if (sorted.length <= STRIP_SYMBOLS) return sorted;
  return [...sorted.slice(0, 2), ...sorted.slice(-2)];
}

function DiscordMark() {
  return (
    <svg className="v5-discord-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.317 4.369a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.009c.12.099.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.891.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.331c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
  );
}

function Para({ text, index, lead }: { text: string; index: number; lead?: boolean }) {
  const [ref, seen] = useInView<HTMLParagraphElement>(0.25);
  return (
    <p
      ref={ref}
      className={lead ? 'v5-p v5-p-lead' : 'v5-p'}
      data-shown={seen ? 'true' : 'false'}
      style={{ transitionDelay: `${(index % 4) * 90}ms` }}
    >
      {text}
    </p>
  );
}

function FundingStrip() {
  const [rows, setRows] = useState<FundingRow[]>(() => pickExtremes(FUNDING_FALLBACK.rows ?? []));
  const [degraded, setDegraded] = useState(true);
  const [now, setNow] = useState(() => Date.now());
  const [ref, seen] = useInView<HTMLDivElement>(0.2);

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();

    const pull = () => {
      if (document.visibilityState === 'hidden') return;
      loadWithFallback<FundingResponse>(basePath('/api/funding'), FUNDING_FALLBACK, controller.signal)
        .then((result) => {
          if (!alive) return;
          const next = pickExtremes(result.data.rows ?? []);
          if (next.length > 0) setRows(next);
          const stale = result.degraded || result.data.meta.degraded;
          setDegraded(stale);
          log.debug('demo', `funding rows=${next.length} source=${result.data.meta.source} degraded=${stale}`);
        })
        .catch((err: unknown) => {
          if (err instanceof DOMException && err.name === 'AbortError') return;
          log.warn('demo', 'funding refresh failed', err);
        });
    };

    pull();
    const poll = window.setInterval(pull, 15_000);
    const tick = window.setInterval(() => setNow(Date.now()), 1000);
    return () => {
      alive = false;
      controller.abort();
      window.clearInterval(poll);
      window.clearInterval(tick);
    };
  }, []);

  const peak = Math.max(...rows.map((r) => Math.abs(r.annualisedRate)), 0.02);

  return (
    <div ref={ref} className="v5-funding" data-shown={seen ? 'true' : 'false'}>
      <div className="v5-funding-head">
        <span className="v5-label">Funding, right now</span>
        <span className="v5-feed" data-live={degraded ? 'false' : 'true'}>
          {degraded ? 'cached' : 'live'}
        </span>
      </div>
      <ul className="v5-funding-list">
        {rows.map((row, i) => {
          const positive = row.fundingRate >= 0;
          const width = clamp((Math.abs(row.annualisedRate) / peak) * 100, 5, 100);
          return (
            <li className="v5-funding-row" key={row.symbol} style={{ transitionDelay: `${i * 80}ms` }}>
              <span className="v5-funding-sym">{row.base}</span>
              <span className="v5-funding-rate" data-dir={positive ? 'up' : 'down'}>
                {fmtRate(row.fundingRate, 4)}
                <em> every {row.fundingIntervalHours}h</em>
              </span>
              <span className="v5-funding-bar" aria-hidden="true">
                <span
                  className="v5-funding-fill"
                  data-dir={positive ? 'up' : 'down'}
                  style={{ width: seen ? `${width}%` : '0%' }}
                />
              </span>
              <span className="v5-funding-apr" data-dir={positive ? 'up' : 'down'}>
                {fmtPct(row.annualisedRate * 100, 2)}
                <em> a year</em>
              </span>
              <span className="v5-funding-next">
                {fmtCountdown(Math.max(0, row.nextFundingTime - now))}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function Site() {
  const [book, setBook] = useState<BookState>({ live: false, source: 'cached' });
  const [copied, setCopied] = useState<'idle' | 'done' | 'fail'>('idle');
  const copyTimer = useRef(0);
  const [bookRef, bookSeen] = useInView<HTMLDivElement>(0.15);

  const onState = useCallback((next: BookState) => setBook(next), []);

  useEffect(() => () => window.clearTimeout(copyTimer.current), []);

  const copyDiscord = () => {
    const reset = () => {
      window.clearTimeout(copyTimer.current);
      copyTimer.current = window.setTimeout(() => setCopied('idle'), 1600);
    };
    const clipboard = navigator.clipboard as Clipboard | undefined;
    if (!clipboard) {
      setCopied('fail');
      reset();
      return;
    }
    clipboard
      .writeText(identity.discord)
      .then(() => {
        setCopied('done');
        reset();
      })
      .catch((err: unknown) => {
        log.warn('app', 'clipboard write failed', err);
        setCopied('fail');
        reset();
      });
  };

  return (
    <div className="v5">
      <div className="v5-wash" aria-hidden="true" />

      <header className="v5-top">
        <span className="v5-top-name">{identity.handle}</span>
      </header>

      <main className="v5-main">
        <section className="v5-hero">
          <h1 className="v5-name">
            <Chars text={identity.name} step={36} />
          </h1>
          <p className="v5-opening">
            <Chars text={story.opening} step={9} delay={260} />
          </p>
        </section>

        <section className="v5-prose">
          {story.paragraphs.map((text, i) => (
            <Para key={text} text={text} index={i} lead={i === 0} />
          ))}
        </section>

        <div ref={bookRef} className="v5-tape" data-shown={bookSeen ? 'true' : 'false'}>
          <div className="v5-tape-head">
            <span className="v5-label">Bitcoin order book, live depth</span>
            <span className="v5-feed" data-live={book.live ? 'true' : 'false'}>
              {book.live ? book.source : 'cached'}
            </span>
          </div>
          <Book onState={onState} />
        </div>

        <section className="v5-prose">
          <Para text={story.markets} index={0} />
          <FundingStrip />
        </section>

        <section className="v5-prose">
          {story.closing.map((text, i) => (
            <Para key={text} text={text} index={i} />
          ))}
        </section>

        <section className="v5-contact">
          <button className="v5-discord" onClick={copyDiscord} data-state={copied}>
            <DiscordMark />
            <span className="v5-discord-handle">
              {copied === 'done' ? 'Copied' : copied === 'fail' ? 'Copy failed' : identity.discord}
            </span>
          </button>
          <a
            className="v5-lab-link"
            href="/make"
            onClick={(event) => {
              if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
              event.preventDefault();
              navigate('/make');
            }}
          >
            View what I can make
            <span className="v5-lab-arrow" aria-hidden="true">
              &#8599;
            </span>
          </a>
          <p className="v5-status" role="status" aria-live="polite">
            {copied === 'done'
              ? 'Discord handle copied to clipboard'
              : copied === 'fail'
                ? 'Copy failed'
                : ''}
          </p>
        </section>
      </main>

    </div>
  );
}
